import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { WaScope } from '../../api/types';
import {
  useWaCampaigns,
  useWaConfig,
  useWaContacts,
  useWaDisconnect,
  useWaEraseContact,
  useWaOptOut,
  useWaRotateOptinToken,
  useWaSync,
  useWaTemplates,
} from '../../api/whatsapp';
import { errorMessage } from '../../api/client';
import type { WhatsappConfig, WhatsappContact } from '../../api/types';
import { Alert, Badge, Button, Card, formatDate } from '../ui';
import { EmptyState, ErrorState, SkeletonTable } from '../states';
import { DataTable } from '../table/DataTable';
import { AnalyticsPanel } from './AnalyticsPanel';
import { CampaignComposer } from './CampaignComposer';
import { ContactImporter } from './ContactImporter';
import { TemplateBuilder } from './TemplateBuilder';
import { WhatsAppConnectButton } from './WhatsAppConnectButton';
import { ACCOUNT_STATUS_COPY, ConsentBadge, QualityBadge } from './shared';

type Section = 'overview' | 'contacts' | 'templates' | 'campaigns' | 'analytics';

interface Notice {
  kind: 'success' | 'info' | 'error';
  text: ReactNode;
}

/**
 * Everything WhatsApp for one tenant, mounted by the portal (own tenant) and
 * the console (selected tenant). Gates itself: the hub must be configured and
 * the tenant must hold a live WhatsApp plan; then the number is connected,
 * and the sections light up.
 */
export function WhatsAppWorkspace({
  scope = {},
  canManage,
  canConnect,
  isStaff = false,
  plansPath = '/portal/subscription',
}: {
  scope?: WaScope;
  canManage: boolean;
  canConnect: boolean;
  isStaff?: boolean;
  plansPath?: string;
}) {
  const [notice, setNotice] = useState<Notice | null>(null);
  const [section, setSection] = useState<Section>('overview');
  const configQuery = useWaConfig(scope);
  const hasAccount = !!configQuery.data?.account;
  const templatesQuery = useWaTemplates({}, scope, { enabled: hasAccount });
  const campaignsQuery = useWaCampaigns({ limit: 50 }, scope, { enabled: hasAccount });
  const contactsQuery = useWaContacts({ limit: 50 }, scope, { enabled: hasAccount });

  const notify = (text: ReactNode, kind: Notice['kind'] = 'success'): void =>
    setNotice({ kind, text });

  if (configQuery.isError)
    return (
      <ErrorState
        error={configQuery.error}
        onRetry={() => configQuery.refetch()}
        retrying={configQuery.isFetching}
      />
    );
  if (configQuery.isPending)
    return (
      <Card title="Your WhatsApp number">
        <SkeletonTable rows={3} cols={2} />
      </Card>
    );

  const config = configQuery.data;
  const templates = templatesQuery.data ?? null;
  const campaigns = campaignsQuery.data?.data ?? null;
  const contacts = contactsQuery.data?.data ?? null;
  const contactsTotal = contactsQuery.data?.pagination.total ?? 0;

  if (!config.configured) {
    return (
      <Alert kind="info">
        WhatsApp is not set up on this hub yet
        {isStaff ? ' (HUB_CRYPTO_KEY and the META_* variables)' : ''}. Nothing to do on your side.
      </Alert>
    );
  }
  if (!config.featureEnabled) {
    return (
      <Card
        title="WhatsApp Business messaging"
        subtitle="Provided by Balsam Solutions on the Meta WhatsApp Cloud API"
      >
        <p>
          Your own WhatsApp Business number, consent-first contacts, templates approved by Meta,
          campaigns for inventory drops and order updates, and delivery analytics. It needs a plan.
        </p>
        <p>
          <Link to={plansPath}>See the plans</Link>
          {isStaff
            ? ' and assign one under Subscriptions.'
            : ', then ask your Balsam contact to switch it on.'}
        </p>
      </Card>
    );
  }

  const account = config.account;
  return (
    <div className="stack">
      {notice ? <Alert kind={notice.kind}>{notice.text}</Alert> : null}
      <AccountCard
        config={config}
        scope={scope}
        canConnect={canConnect}
        isStaff={isStaff}
        onChanged={notify}
      />
      {account && account.status !== 'DISCONNECTED' ? (
        <>
          <div className="segmented" role="tablist" aria-label="WhatsApp sections">
            {(
              [
                ['overview', 'Overview'],
                ['contacts', `Contacts (${contactsTotal})`],
                ['templates', 'Templates'],
                ['campaigns', 'Campaigns'],
                ['analytics', 'Analytics'],
              ] as Array<[Section, string]>
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-pressed={section === key}
                aria-selected={section === key}
                onClick={() => setSection(key)}
              >
                {label}
              </button>
            ))}
          </div>
          {templatesQuery.isError || campaignsQuery.isError || contactsQuery.isError ? (
            <ErrorState
              error={templatesQuery.error ?? campaignsQuery.error ?? contactsQuery.error}
              onRetry={() => {
                if (templatesQuery.isError) void templatesQuery.refetch();
                if (campaignsQuery.isError) void campaignsQuery.refetch();
                if (contactsQuery.isError) void contactsQuery.refetch();
              }}
              retrying={
                templatesQuery.isFetching || campaignsQuery.isFetching || contactsQuery.isFetching
              }
            />
          ) : null}
          {section === 'overview' ? (
            <Card title="At a glance">
              <div className="stats">
                <StatBlock label="Contacts" value={contactsTotal} hint="all consent states" />
                <StatBlock
                  label="Templates approved"
                  value={(templates ?? []).filter((t) => t.status === 'APPROVED').length}
                  hint={`${templates?.length ?? 0} total`}
                />
                <StatBlock
                  label="Campaigns"
                  value={campaigns?.length ?? 0}
                  hint={`${(campaigns ?? []).filter((c) => c.status === 'SENDING').length} sending`}
                />
                <StatBlock
                  label="Send rate"
                  value={`${config.sendRatePerSecond}/s`}
                  hint="per number"
                />
              </div>
            </Card>
          ) : null}
          {section === 'contacts' ? (
            <>
              {canManage ? (
                <Card
                  title="Import contacts"
                  subtitle="Identity and consent are stored together; the number is encrypted at rest."
                >
                  <ContactImporter scope={scope} onImported={(_r, text) => notify(text, 'info')} />
                </Card>
              ) : null}
              <Card
                title="Contacts"
                subtitle={`${contactsTotal} total · newest first`}
                className="card--table"
              >
                {contactsQuery.isPending ? <SkeletonTable rows={3} cols={6} /> : null}
                {contacts && contacts.length === 0 ? (
                  <EmptyState
                    title="No contacts yet"
                    body={
                      canManage
                        ? 'Import a CSV above or collect opt-ins from your website; every contact arrives with its consent.'
                        : 'Contacts appear here once the owner imports them or opt-ins arrive from the website.'
                    }
                  />
                ) : null}
                {contacts && contacts.length > 0 ? (
                  <DataTable
                    label="Contacts"
                    columns={[
                      { key: 'number', header: 'Number' },
                      { key: 'name', header: 'Name' },
                      { key: 'consent', header: 'Consent' },
                      { key: 'categories', header: 'Categories' },
                      { key: 'source', header: 'Source' },
                      { key: 'opted_in', header: 'Opted in' },
                      { key: 'actions', header: 'Actions', srOnly: true },
                    ]}
                  >
                    {contacts.map((c) => (
                      <ContactRow
                        key={c.id}
                        contact={c}
                        scope={scope}
                        canManage={canManage}
                        onChanged={notify}
                      />
                    ))}
                  </DataTable>
                ) : null}
              </Card>
            </>
          ) : null}
          {section === 'templates' ? (
            <Card>
              <TemplateBuilder
                templates={templates}
                scope={scope}
                canManage={canManage}
                onChanged={notify}
              />
            </Card>
          ) : null}
          {section === 'campaigns' ? (
            <Card>
              <CampaignComposer
                templates={templates}
                campaigns={campaigns}
                scope={scope}
                canManage={canManage}
                onChanged={notify}
              />
            </Card>
          ) : null}
          {section === 'analytics' ? (
            <Card title="Analytics">
              <AnalyticsPanel account={account} campaigns={campaigns} scope={scope} />
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function StatBlock({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
      {hint ? <div className="stat__hint">{hint}</div> : null}
    </div>
  );
}

function AccountCard({
  config,
  scope,
  canConnect,
  isStaff,
  onChanged,
}: {
  config: WhatsappConfig;
  scope: WaScope;
  canConnect: boolean;
  isStaff: boolean;
  onChanged: (text: ReactNode, kind?: Notice['kind']) => void;
}) {
  const account = config.account;
  const sync = useWaSync(scope);
  const disconnect = useWaDisconnect(scope);
  const rotate = useWaRotateOptinToken(scope);
  const refresh = () =>
    sync.mutate(undefined, {
      onSuccess: (a) =>
        onChanged(
          <>
            Refreshed <strong>{a.displayPhoneNumber ?? a.phoneNumberId}</strong> from Meta.
          </>,
          'info',
        ),
    });
  const confirmDisconnect = () => {
    if (
      !window.confirm('Disconnect this number? Campaigns pause; contacts and consent records stay.')
    )
      return;
    disconnect.mutate(undefined, {
      onSuccess: () => onChanged('Number disconnected.', 'info'),
    });
  };
  const token = rotate.data?.token;
  const error = sync.error ?? disconnect.error ?? rotate.error;

  return (
    <Card
      title="Your WhatsApp number"
      subtitle={
        config.subscription ? (
          <>
            Plan <strong>{config.subscription.plan.name}</strong>
            {config.subscription.status === 'TRIALING' && config.subscription.trialEnd
              ? ` · trial ends ${formatDate(config.subscription.trialEnd)}`
              : ''}
          </>
        ) : undefined
      }
      actions={
        account && account.status !== 'DISCONNECTED' && canConnect ? (
          <span className="table__actions">
            <Button
              variant="secondary"
              className="btn--small"
              loading={sync.isPending}
              onClick={refresh}
            >
              Refresh from Meta
            </Button>
            <Button
              variant="danger"
              className="btn--small"
              loading={disconnect.isPending}
              onClick={confirmDisconnect}
            >
              Disconnect
            </Button>
          </span>
        ) : undefined
      }
    >
      {error ? <Alert kind="error">{errorMessage(error)}</Alert> : null}
      {!account || account.status === 'DISCONNECTED' ? (
        canConnect ? (
          <div className="stack">
            <p>
              Connect the number your business will message from. Meta's Embedded Signup creates or
              links your WhatsApp Business Account; the hub keeps the access token in its secrets
              store.
            </p>
            <WhatsAppConnectButton
              config={config}
              scope={scope}
              allowManual={isStaff}
              onConnected={(_a, text) => onChanged(text)}
            />
          </div>
        ) : (
          <p className="muted">No number connected yet. The owner connects it.</p>
        )
      ) : (
        <div className="stack">
          <dl className="kv">
            <dt>Number</dt>
            <dd>
              <strong>{account.displayPhoneNumber ?? account.phoneNumberId}</strong>
              {account.verifiedName ? (
                <span className="muted"> · {account.verifiedName}</span>
              ) : null}
            </dd>
            <dt>Status</dt>
            <dd className="badges">
              <Badge tone={ACCOUNT_STATUS_COPY[account.status].tone}>
                {ACCOUNT_STATUS_COPY[account.status].label}
              </Badge>
              <QualityBadge rating={account.qualityRating} />
              {account.messagingLimit ? (
                <Badge tone="neutral">{account.messagingLimit.replace('TIER_', 'limit ')}</Badge>
              ) : null}
              {account.webhookSubscribed ? (
                <Badge tone="ok">Webhook on</Badge>
              ) : (
                <Badge tone="warn">No webhook</Badge>
              )}
              {account.registered ? (
                <Badge tone="ok">Registered</Badge>
              ) : (
                <Badge tone="warn">Not registered</Badge>
              )}
            </dd>
            <dt>Ids</dt>
            <dd>
              <code title="WhatsApp Business Account">{account.wabaId}</code>{' '}
              <code title="Phone number id">{account.phoneNumberId}</code>
            </dd>
            <dt>Last synced</dt>
            <dd>{formatDate(account.lastSyncedAt)}</dd>
            {account.lastError ? (
              <>
                <dt>Note</dt>
                <dd className="muted">{account.lastError}</dd>
              </>
            ) : null}
          </dl>
          {canConnect ? (
            <div className="stack">
              <div className="form__row">
                <Button
                  variant="secondary"
                  className="btn--small"
                  loading={rotate.isPending}
                  onClick={() => rotate.mutate()}
                >
                  {account.hasOptinToken
                    ? 'Rotate website opt-in token'
                    : 'Create website opt-in token'}
                </Button>
                <span className="muted">
                  Lets your website or checkout post opt-ins to <code>/whatsapp/public/optin</code>{' '}
                  with <code>X-Optin-Token</code>.
                </span>
              </div>
              {token ? (
                <div className="secret">
                  <code className="secret__value">{token}</code>
                  <span className="muted">Shown once.</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function ContactRow({
  contact,
  scope,
  canManage,
  onChanged,
}: {
  contact: WhatsappContact;
  scope: WaScope;
  canManage: boolean;
  onChanged: (text: ReactNode, kind?: Notice['kind']) => void;
}) {
  const optOut = useWaOptOut(scope);
  const erase = useWaEraseContact(scope);
  const unsubscribe = () =>
    optOut.mutate(
      { id: contact.id },
      {
        onSuccess: () =>
          onChanged(
            <>
              Unsubscribed <code>{contact.phoneMasked}</code>.
            </>,
            'info',
          ),
      },
    );
  const confirmErase = () => {
    if (
      !window.confirm(
        `Erase ${contact.phoneMasked}? Their number, name and email are wiped now; the consent record is kept.`,
      )
    )
      return;
    erase.mutate(contact.id, {
      onSuccess: () =>
        onChanged(
          <>
            Erased <code>{contact.phoneMasked}</code>.
          </>,
          'info',
        ),
    });
  };
  const error = optOut.error ?? erase.error;
  return (
    <>
      <tr>
        <td>
          <code>{contact.phoneMasked}</code>
        </td>
        <td>
          {contact.name ?? <span className="muted">—</span>}
          {contact.emailMasked ? <div className="muted">{contact.emailMasked}</div> : null}
        </td>
        <td>
          <ConsentBadge status={contact.status} />
        </td>
        <td>{contact.categoriesConsented.join(', ') || '—'}</td>
        <td>{contact.optInSource ?? '—'}</td>
        <td>{formatDate(contact.optInAt)}</td>
        <td className="table__actions">
          {canManage && contact.status === 'SUBSCRIBED' ? (
            <Button
              variant="secondary"
              className="btn--small"
              loading={optOut.isPending}
              onClick={unsubscribe}
            >
              Unsubscribe
            </Button>
          ) : null}
          {canManage ? (
            <Button
              variant="danger"
              className="btn--small"
              loading={erase.isPending}
              onClick={confirmErase}
            >
              Erase
            </Button>
          ) : null}
        </td>
      </tr>
      {error ? (
        <tr className="table__detail">
          <td colSpan={7}>
            <Alert kind="error">{errorMessage(error)}</Alert>
          </td>
        </tr>
      ) : null}
    </>
  );
}
